import styled from 'styled-components'
import { CardElement, useElements } from '@stripe/react-stripe-js'
import { Form, Formik, FormikProps } from 'formik'
import { useEffect, useState } from 'react'

import AddressFormField, { FieldType } from 'checkout/components/AddressFormField'
import AddressSummaryDisplay from 'checkout/components/ShippingAddressPanel/AddressSummaryDisplay'
import CountryDropdownField from 'checkout/components/CountryDropdownField'
import CreditCardInput from 'checkout/components/PaymentMethodPanel/CreditCardInput'
import Divider from 'shared/components/Divider'
import Spinner from 'shared/components/Spinner'
import StateDropdownField from 'checkout/components/StateDropdownField'
import { Address } from 'shared/types/Address'
import { Button } from 'shared/components/Button'
import { CheckoutQA } from 'shared/dataAttributes'
import { SVGIcon } from 'shared/components/Icons'
import { colors, fonts } from 'shared/lib'
import { getAddressValidationSchema } from 'shared/addressValidations'
import { t } from 'localization'
import { useAddressFormContext } from 'checkout/containers/AddressFormContextProvider'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useGetAddressQuery } from 'api/addressesApi'

const TP = 'checkout.panels.PaymentMethod.NewCardNewAddressForm'

const addressDefault: Address = {
  name: '',
  address1: '',
  address2: undefined,
  city: '',
  postalCode: '',
  countryCode: '',
  state: '',
  phone: undefined,
}

export const NewCardNewAddressForm = () => {
  const elements = useElements()
  const { addNewCreditCardRTK, order, panels, updatePanel } = useCheckoutContext()
  const [isSameAsShipping, setIsSameAsShipping] = useState(true)
  const [isStripeComplete, setIsStripeComplete] = useState(false)
  const [initialValues, setInitialValues] = useState(addressDefault)
  const {
    availableCountries,
    isStateRequired,
    isZipRequired,
    countryISOCode,
    countryUpdateHandler,
  } = useAddressFormContext()

  const { data: shippingAddress, isSuccess } = useGetAddressQuery(order?.addressId)

  const handleShippingAddressToggle =
    (
      currentFormValues: Address,
      setFormValues: (values: Address, shouldValidate?: boolean) => void,
    ) =>
    () => {
      const mergedValues = {
        name: currentFormValues?.name || '',
        address1: shippingAddress?.address1 || '',
        address2: shippingAddress?.address2 || '',
        city: shippingAddress?.city || '',
        postalCode: shippingAddress?.postalCode || '',
        countryCode: shippingAddress?.countryCode || '',
        state: shippingAddress?.state || '',
        phone: shippingAddress?.phone || '',
      } as Address
      setFormValues(mergedValues, true)
      countryUpdateHandler(mergedValues.countryCode)
      setIsSameAsShipping(!isSameAsShipping)
    }

  // update form fields with new country code and clear state field
  const handleCountryChange = (
    countryCode: string,
    currentFormValues: Address,
    setFormValues: (values: Address) => void,
  ) => {
    countryUpdateHandler(countryCode)
    setFormValues({ ...currentFormValues, countryCode, state: '' })
  }

  const handleCancelClick = () => {
    const cardElement = elements?.getElement(CardElement)
    cardElement?.blur()
    updatePanel('paymentMethod', { view: 'saved' })
  }

  const handleFormComplete = async (isComplete, validateForm) => {
    setIsStripeComplete(isComplete)
    await validateForm()
  }

  useEffect(() => {
    if (isSuccess) {
      const value = {
        name: '',
        address1: shippingAddress?.address1 || '',
        address2: shippingAddress?.address2 || '',
        city: shippingAddress?.city || '',
        postalCode: shippingAddress?.postalCode || '',
        countryCode: shippingAddress?.countryCode || '',
        state: shippingAddress?.state || '',
        phone: shippingAddress?.phone || '',
      } as Address
      setInitialValues(value)
    }
  }, [isSuccess])

  return (
    <Spinner
      showSpinner={panels.paymentMethod.loading}
      text={t(`${TP}.spinnerText`, 'Saving payment info...')}
      subText={t(`${TP}.spinnerSubText`, 'Please do not close your browser!')}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={addNewCreditCardRTK}
        validationSchema={getAddressValidationSchema(isZipRequired, isStateRequired)}
      >
        {({ isValid, values, setValues, validateForm }: FormikProps<Address>) => {
          return (
            <Form>
              <AddressFormField
                type={FieldType.FullName}
                title={t(`${TP}.name`, '* Name on Card')}
                name="name"
                qaAttr={CheckoutQA.BillingNameBox}
              />
              <CreditCardInput
                title={t(`${TP}.creditCard`, '* Credit Card')}
                onComplete={(isComplete) => handleFormComplete(isComplete, validateForm)}
                qaAttr={CheckoutQA.NewCreditCardBox}
              />
              <SameShipping>
                <CheckboxLabel>
                  {isSameAsShipping ? (
                    <SVGIcon color={colors.FC2_BLACK} name="circleCheck" size="20" />
                  ) : (
                    <Circle data-testid="circle" />
                  )}
                  {t(`${TP}.billingSameAsShipping`, 'Billing address same as shipping')}
                  <input
                    checked={isSameAsShipping}
                    data-qa={CheckoutQA.BillingSameAsShippingCheckbox}
                    hidden
                    onClick={handleShippingAddressToggle(values, setValues)}
                    type="checkbox"
                  />
                </CheckboxLabel>
              </SameShipping>
              <Divider />
              {isSameAsShipping ? (
                <BillingAddressSummary qaAttr="BillingAddress" {...shippingAddress} />
              ) : (
                <>
                  <AddressFormField
                    disabled={isSameAsShipping}
                    type={FieldType.StreetAddress}
                    title={t(`${TP}.streetAddress`, '* Street Address')}
                    name="address1"
                    qaAttr={CheckoutQA.BillingAddress1Box}
                  />
                  <AddressFormField
                    disabled={isSameAsShipping}
                    type={FieldType.UnitNumber}
                    title={t(`${TP}.apartment`, 'Apartment, Suite, Unit, Building, Floor, etc.')}
                    name="address2"
                    qaAttr={CheckoutQA.BillingAddress2Box}
                  />
                  <AddressFormField
                    disabled={isSameAsShipping}
                    type={FieldType.City}
                    title={t(`${TP}.city`, '* City')}
                    name="city"
                    qaAttr={CheckoutQA.BillingCityBox}
                  />
                  <AddressFormField
                    disabled={isSameAsShipping}
                    type={FieldType.ZipCode}
                    title={t(`${TP}.zipCode`, {
                      defaultValue: '{isFieldRquired}Zip / Postal Code',
                      isFieldRquired: isZipRequired ? '* ' : '',
                    })}
                    name="postalCode"
                    qaAttr={CheckoutQA.BillingZipCodeBox}
                  />
                  <CountryDropdownField
                    availableCountries={availableCountries}
                    disabled={isSameAsShipping}
                    title={t(`${TP}.countryCode`, '* Country or Region')}
                    name="countryCode"
                    qaAttr={CheckoutQA.BillingCountryBox}
                    onChange={(countryCode: string) =>
                      handleCountryChange(countryCode, values, setValues)
                    }
                  />
                  {isStateRequired && (
                    <StateDropdownField
                      disabled={isSameAsShipping}
                      countryISOCode={countryISOCode}
                      title={t(`${TP}.state`, '* State')}
                      name="state"
                      qaAttr={CheckoutQA.BillingStateBox}
                      useStateCodeAsValue
                    />
                  )}
                  <AddressFormField
                    disabled={isSameAsShipping}
                    type={FieldType.Telephone}
                    title={t(`${TP}.telephone`, 'Telephone')}
                    name="phone"
                    qaAttr={CheckoutQA.BillingPhoneBox}
                  />
                  <Pointer>{t(`${TP}.requiredFields`, '* Required Fields')}</Pointer>
                </>
              )}
              <Button
                $fill
                buttonType="primary1"
                type="submit"
                disabled={!isValid || !isStripeComplete}
                qaAttr={CheckoutQA.NewCardContinueButton}
              >
                {t(`${TP}.continue`, 'Continue')}
              </Button>
              <CancelButton
                $fill
                buttonType="secondary"
                qaAttr={CheckoutQA.NewCardCancelButton}
                onClick={handleCancelClick}
              >
                {order?.billingInfoId
                  ? t(`${TP}.cancel`, 'Cancel')
                  : t(`${TP}.otherPaymentMethods`, 'Other Payment Methods')}
              </CancelButton>
            </Form>
          )
        }}
      </Formik>
    </Spinner>
  )
}

const Pointer = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  margin: 5px 0 30px;
`

const CheckboxLabel = styled.label`
  align-items: center;
  display: flex;
  margin-left: 10px;
  ${fonts.SUBTITLE_3}
`

const SameShipping = styled.div`
  display: flex;
  margin: 20px 0 25px;
  align-items: center;
`

const CancelButton = styled(Button)`
  margin-top: 10px;
`

const BillingAddressSummary = styled(AddressSummaryDisplay)`
  margin-bottom: 30px;
`

const Circle = styled.div`
  border: 1px solid ${colors.FC2_BLACK};
  border-radius: 50%;
  display: inline-block;
  margin: 0px 1px 4px 1px;
  height: 19px;
  width: 19px;
`
